import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { logActivity } from './useActivityLog';
import { useAuth } from '@/contexts/AuthContext';

export const useStaffManagement = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  // Fetch all staff/users - only fetch when authenticated and NOT in demo mode
  const { data: staff = [], isLoading } = useQuery({
    queryKey: ['staff'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('list-users');
      
      if (error) throw error;
      return data?.users || [];
    },
    enabled: !!user
  });

  const createStaff = async (staffData: {
    email: string;
    fullName: string;
    role: string;
    siteIds: string[];
    phone?: string;
    position?: string;
  }) => {
    setIsCreating(true);
    try {
      
      const { data: userData, error: userError } = await supabase.functions.invoke('create-user', {
        body: {
          email: staffData.email,
          full_name: staffData.fullName,
          role: staffData.role,
          site_ids: staffData.siteIds,
          phone: staffData.phone,
          position: staffData.position
        }
      });

      if (userError) throw userError;

      await logActivity({
        actionType: 'create_user',
        actionDetails: {
          email: staffData.email,
          role: staffData.role,
          sites: staffData.siteIds
        }
      });

      toast.success('Staff member created successfully');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      
      return { 
        success: true, 
        data: {
          ...userData,
          tempPassword: userData?.tempPassword
        }
      };
    } catch (error: any) {
      console.error('Create staff error:', error);
      const errorMessage = error.message || 'Failed to create staff member';
      
      // Check for rate limit error
      if (errorMessage.includes('50 seconds') || errorMessage.includes('rate limit')) {
        toast.error('Please wait 50 seconds between creating new users (security requirement)');
      } else {
        toast.error(errorMessage);
      }
      return { success: false, error: error.message };
    } finally {
      setIsCreating(false);
    }
  };

  // Update staff member
  const updateStaff = async (userId: string, updates: {
    fullName?: string;
    role?: string;
    siteIds?: string[];
    phone?: string;
    position?: string;
    isActive?: boolean;
  }) => {
    try {
      const { error } = await supabase.functions.invoke('update-user', {
        body: {
          user_id: userId,
          ...updates
        }
      });

      if (error) throw error;

      await logActivity({
        actionType: 'update_user',
        actionDetails: {
          user_id: userId,
          updates
        }
      });

      toast.success('Staff member updated');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to update staff member');
      return { success: false, error: error.message };
    }
  };

  // Deactivate staff
  const deactivateStaff = async (userId: string) => {
    try {
      const { error } = await supabase.functions.invoke('update-user', {
        body: {
          user_id: userId,
          is_active: false
        }
      });

      if (error) throw error;

      await logActivity({
        actionType: 'update_user',
        actionDetails: {
          user_id: userId,
          action: 'deactivated'
        }
      });

      toast.success('Staff member deactivated');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to deactivate staff member');
      return { success: false };
    }
  };

  // Reset password
  const resetPassword = async (userId: string, newPassword: string, options?: { forceReset?: boolean }) => {
    try {
      const { error } = await supabase.functions.invoke('update-user-password', {
        body: {
          user_id: userId,
          password: newPassword, // Match what edge function expects
          force_reset: options?.forceReset ?? true
        }
      });

      if (error) throw error;

      await logActivity({
        actionType: 'reset_password',
        actionDetails: {
          user_id: userId
        }
      });

      toast.success('Password reset successfully');
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset password');
      return { success: false };
    }
  };

  const resetTestingPasswords = async (userIds: string[], newPassword: string) => {
    try {
      await Promise.all(userIds.map((userId) =>
        supabase.functions.invoke('update-user-password', {
          body: {
            user_id: userId,
            password: newPassword,
            force_reset: false
          }
        }).then(({ error }) => {
          if (error) throw error;
        })
      ));

      await logActivity({
        actionType: 'reset_password',
        actionDetails: {
          action: 'role_testing_password_reset',
          user_count: userIds.length
        }
      });

      toast.success('Role testing passwords reset');
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset role testing passwords');
      return { success: false, error: error.message };
    }
  };

  // Restore (reactivate) staff
  const restoreStaff = async (userId: string) => {
    try {
      const { error } = await supabase.functions.invoke('update-user', {
        body: {
          user_id: userId,
          is_active: true
        }
      });

      if (error) throw error;

      await logActivity({
        actionType: 'update_user',
        actionDetails: {
          user_id: userId,
          action: 'restored'
        }
      });

      toast.success('Staff member restored');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to restore staff member');
      return { success: false };
    }
  };

  return {
    staff,
    isLoading,
    isCreating,
    createStaff,
    updateStaff,
    deactivateStaff,
    restoreStaff,
    resetPassword,
    resetTestingPasswords
  };
};
